"use client";

import { cn } from "@/lib/utils";
import { pushGtmEvent } from "@/utils/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

/**
 * NavBar 1060 — KUK site nav (kukonline.ac.in `organisms/Header`).
 *
 * White bar: the university crest and wordmark on the left, the site links in
 * the middle — three of which (Programmes, Student Corner, Admission) open
 * dropdowns, and Programmes expands a third level of specialisations inline —
 * and a single brand-filled CTA on the right. The current page's link carries a
 * grey pill rather than a colour change, which is what separates this from
 * NavBar 1047.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px) — the nearest
 * real breakpoint to 1024px.
 *
 * THREE BARS BECOME TWO. v1 ships three near-duplicate DOM blocks (≥1024,
 * 768–1023, <768). The first two differ only in bar height, logo composition and
 * padding, so they are one responsive block here; the hamburger bar below 768px
 * stays separate, as it does in v1.
 *
 * THE TWO LOGOS. v1 renders a crest and a separate wordmark side by side, and
 * drops the wordmark in the 768–1023 band where the nav needs the room. Both are
 * CMS fields: `logo_icon` is the crest (always shown) and `mobile_logo_icon` is
 * the wordmark (shown below 768px and from 1080px up, hidden in between) —
 * exactly v1's arrangement.
 *
 * MENU DATA. The links come from the same `menu_item` tree every other nav
 * variant reads, so the reference's hardcoded `publicRoutes` array and its
 * separate `/programs-menu` fetch both become ordinary CMS rows. A third level
 * (specialisations under a programme) is supported at any position, not only
 * under Programmes.
 *
 * NOT CARRIED OVER — v1's account state (the Sign Up / avatar / logout swap and
 * its cookie reads), its internship apply-form modal, and its `pageshow`
 * bfcache handler that hard-reloads the window. None of those are page content,
 * and the reload in particular throws away the visitor's scroll position on
 * every Back.
 *
 * NOT CARRIED OVER — the duplicate "Programmes" trigger v1 puts on the RIGHT of
 * the mobile bar, beside Sign Up, alongside the same menu inside the drawer. It
 * would need the CMS to nominate one privileged menu item to be worth having;
 * every link is reachable from the drawer.
 */

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  icon_alt?: string;
  text_color?: string;
  children?: MenuItem[];
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  textColor?: string;
  icon?: string;
  iconAlt?: string;
  children: RouteItem[];
}

/* ------------------------------------------------------------------ */
/* Chrome baked into the reference (not CMS fields)                     */
/* ------------------------------------------------------------------ */

/** `text-gray-600` — third-level (specialisation) label colour. */
const SUBITEM_TEXT = "#475467";

/**
 * Intrinsic sizes of the two logo files shipped in `public/icons`
 * (`kuk-logo.png` and `kuk-university-print.png`, byte-identical to v1's
 * `icons/logo.png` and `icons/university_print.png`). next/image needs the real
 * aspect ratio to build a correct srcset — the rendered size comes from CSS
 * (31px wide for the crest, 32px tall for the wordmark), matching v1.
 */
const CREST = { width: 31, height: 36 };
const WORDMARK = { width: 540, height: 156 };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

const isExternal = (href: string) =>
  /^https?:\/\//i.test(href) ||
  href.startsWith("//") ||
  href.startsWith("tel:") ||
  href.startsWith("mailto:");

/**
 * next/image throws on any absolute src whose host is missing from
 * `next.config.mjs`'s host whitelist, which would take the whole page (and the
 * admin page builder) down over one CMS value. That list only carries the
 * per-tenant asset buckets, so an absolute URL from anywhere else bypasses the
 * optimizer instead — the same guard NavBar 1047 / Header 1046 use.
 */
function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

/** Backend menu tree → routes, active-only and priority-sorted, at any depth. */
function toRoutes(items: MenuItem[] | undefined): RouteItem[] {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items
    .filter((item) => item.status)
    .sort((a, b) => a.priority - b.priority)
    .map((item) => ({
      id: item.id,
      route: item.url && item.url.trim() !== "" ? item.url : `/${item.slug}`,
      label: item.title,
      textColor: item.text_color,
      icon: item.icon,
      iconAlt: item.icon_alt,
      children: toRoutes(item.children),
    }));
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const ChevronDown = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden
    className={cn(
      "ml-1 shrink-0 transition-transform duration-300 ease-in-out",
      open ? "rotate-180" : "rotate-0",
    )}
  >
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
    className={cn(
      "shrink-0 transition-transform duration-300",
      open ? "rotate-90" : "rotate-0",
    )}
  >
    <path
      d="M6 3.5L10.5 8L6 12.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HamburgerGlyph = ({ color }: { color: string }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <path d="M4 8h20M4 14h20M4 20h20" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CloseGlyph = ({ color }: { color: string }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <path d="M7 7l14 14M21 7L7 21" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Widget                                                              */
/* ------------------------------------------------------------------ */

const NavBar1060 = ({ data }: { data: any }) => {
  const sectionData = data?.data ?? data;
  const isPreview = Boolean(data?.isPreview ?? false);
  const pathname = usePathname() || "";
  const dispatch = useDispatch();

  const navRef = useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  /** Third level: which dropdown row has its specialisations expanded. */
  const [expandedChild, setExpandedChild] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);

  const {
    /** The crest — shown at every width. */
    logo_icon = "",
    logo_icon_alt = "Logo",
    /** The wordmark — shown below 768px and from 1080px up. */
    mobile_logo_icon = "",
    mobile_logo_icon_alt = "",
    background_color = "#FFFFFF",
    nav_text_color = "#1B1B1B",
    /** v1's `bg-gray-300` pill behind the current page's link. */
    active_menu_color = "#EAECF0",
    border_color = "#EAECF0",
    mobile_menu_text_color = "#1B1B1B",
    dropdown_bg_color = "#FFFFFF",
    dropdown_text_color = "#1B1B1B",
    cta3_label = "Sign Up",
    cta3_href = "",
    cta3_bg_color = "#9F1C33",
    cta3_text_color = "#FFFFFF",
    hamburger_icon = "",
    hamburger_icon_alt = "Menu",
    mobile_cancel_icon = "",
    mobile_cancel_icon_alt = "Close",
    menuItems = [],
  } = sectionData ?? {};

  const showDropdownIcons = toBool(sectionData?.show_dropdown_icons, false);
  const cta3Enabled = toBool(sectionData?.cta3_enabled, true);
  const stickyToTop = toBool(sectionData?.sticky_to_top, true);
  const showBarShadow = toBool(sectionData?.show_bar_shadow, true);

  const routes: RouteItem[] = useMemo(() => toRoutes(menuItems), [menuItems]);

  /** v1 marks "/" active only on an exact match; everything else on prefix. */
  const isRouteActive = (route: string) => {
    if (!route) return false;
    if (route === "/") return pathname === "/";
    const normalized = route.startsWith("/") ? route : `/${route}`;
    return pathname === normalized || pathname.startsWith(`${normalized}/`);
  };

  // Close on outside click, and whenever the route changes (v1 resets on pathname).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
        setExpandedChild(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setExpandedChild(null);
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  }, [pathname]);

  const applyHref = str(cta3_href);

  const handleApplyClick = () => {
    pushGtmEvent("cta_click", {
      section: "navbar",
      label: cta3_label || "sign_up",
      link_url: applyHref,
    });
    if (applyHref) return;
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  /* -- shared pieces -------------------------------------------------- */

  const applyCta = (extraClass?: string) => {
    if (!cta3Enabled || !cta3_label) return null;
    const className = cn(
      "inline-flex h-7 shrink-0 items-center justify-center whitespace-nowrap",
      "rounded-sm px-5 py-2 text-xs font-medium shadow transition-opacity hover:opacity-90",
      extraClass,
    );
    const style = { backgroundColor: cta3_bg_color, color: cta3_text_color };

    if (applyHref) {
      return (
        <a
          href={applyHref}
          onClick={handleApplyClick}
          data-cta-name="sign_up"
          target={isExternal(applyHref) ? "_blank" : undefined}
          rel={isExternal(applyHref) ? "noopener noreferrer" : undefined}
          className={className}
          style={style}
        >
          {cta3_label}
        </a>
      );
    }

    return (
      <button
        type="button"
        onClick={handleApplyClick}
        data-cta-name="sign_up"
        className={cn(className, "cursor-pointer")}
        style={style}
      >
        {cta3_label}
      </button>
    );
  };

  /**
   * v1's logo group: the crest, then the wordmark beside it. The wordmark is
   * dropped in the 768–1079 band, where the nav needs the width.
   */
  const logoGroup = (wrapperClass: string, wordmarkClass: string) => {
    if (!logo_icon && !mobile_logo_icon) return null;
    return (
      <Link
        href="/"
        className={cn("flex cursor-pointer items-center gap-2", wrapperClass)}
        aria-label={logo_icon_alt || "Home"}
      >
        {logo_icon ? (
          <Image
            src={logo_icon}
            alt={logo_icon_alt || "Logo"}
            width={CREST.width}
            height={CREST.height}
            priority
            className="h-auto w-[31px] object-contain"
            unoptimized={shouldUnoptimizeImageSrc(logo_icon)}
          />
        ) : null}
        {mobile_logo_icon ? (
          <Image
            src={mobile_logo_icon}
            alt={mobile_logo_icon_alt || logo_icon_alt || "Logo"}
            width={WORDMARK.width}
            height={WORDMARK.height}
            priority
            className={cn("h-8 w-auto object-contain", wordmarkClass)}
            unoptimized={shouldUnoptimizeImageSrc(mobile_logo_icon)}
          />
        ) : null}
      </Link>
    );
  };

  /* -- desktop / tablet dropdown -------------------------------------- */

  const renderDropdown = (route: RouteItem) => {
    /** Only the menu with a third level gets v1's narrower, left-indented card. */
    const isProgrammeStyle = route.children.some((child) => child.children.length > 0);

    return (
      <div
        className={cn(
          "animate-navbar-dropdown absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2",
          "overflow-hidden rounded-md border p-1 shadow-md",
          isProgrammeStyle ? "min-w-[200px]" : "min-w-[15rem]",
        )}
        style={{ backgroundColor: dropdown_bg_color, borderColor: border_color }}
      >
        {route.children.map((child) => {
          const hasGrandChildren = child.children.length > 0;
          const childActive = isRouteActive(child.route);

          return (
            <div key={child.id} className="w-full">
              {hasGrandChildren ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedChild(expandedChild === child.id ? null : child.id)
                  }
                  className="flex w-full cursor-pointer items-center justify-between rounded-sm pr-2 text-xs"
                  style={{
                    color: child.textColor || dropdown_text_color,
                    backgroundColor: childActive ? active_menu_color : "transparent",
                  }}
                  aria-expanded={expandedChild === child.id}
                >
                  <span className="flex-1 py-2 pl-4 text-left">{child.label}</span>
                  <ChevronRight
                    open={expandedChild === child.id}
                    color={child.textColor || dropdown_text_color}
                  />
                </button>
              ) : (
                <Link
                  href={child.route}
                  target={isExternal(child.route) ? "_blank" : undefined}
                  rel={isExternal(child.route) ? "noopener noreferrer" : undefined}
                  onClick={() => setOpenDropdown(null)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2 rounded-sm py-2 text-xs",
                    isProgrammeStyle ? "pl-4 pr-2" : "px-8",
                  )}
                  style={{
                    color: child.textColor || dropdown_text_color,
                    backgroundColor: childActive ? active_menu_color : "transparent",
                  }}
                >
                  {showDropdownIcons && child.icon ? (
                    <Image
                      src={child.icon}
                      alt={child.iconAlt || ""}
                      width={16}
                      height={16}
                      className="shrink-0 object-contain"
                      unoptimized={shouldUnoptimizeImageSrc(child.icon)}
                    />
                  ) : null}
                  {child.label}
                </Link>
              )}

              {hasGrandChildren && expandedChild === child.id ? (
                <div
                  className="flex w-full flex-col items-start space-y-2 border-y px-0 py-1 transition-all duration-300"
                  style={{
                    backgroundColor: dropdown_bg_color,
                    borderColor: border_color,
                  }}
                >
                  {child.children.map((grandChild) => (
                    <Link
                      key={grandChild.id}
                      href={grandChild.route}
                      onClick={() => {
                        setExpandedChild(null);
                        setOpenDropdown(null);
                      }}
                      className="w-full cursor-pointer px-5 py-1 text-xs"
                      style={{
                        color: grandChild.textColor || SUBITEM_TEXT,
                        backgroundColor: isRouteActive(grandChild.route)
                          ? active_menu_color
                          : "transparent",
                      }}
                    >
                      {grandChild.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  /* -- mobile drawer -------------------------------------------------- */

  const renderMobileMenu = () => (
    <div
      className={cn(
        "animate-navbar-dropdown absolute left-0 top-full z-[100] mt-3 flex max-h-[80vh]",
        "min-w-[15rem] flex-col overflow-y-auto rounded-md border p-1 shadow-md tab:hidden",
      )}
      style={{ backgroundColor: dropdown_bg_color, borderColor: border_color }}
    >
      {routes.map((route) => {
        const active = isRouteActive(route.route);

        if (route.children.length === 0) {
          return (
            <Link
              key={route.id}
              href={route.route}
              onClick={() => setIsMobileMenuOpen(false)}
              className="cursor-pointer rounded-sm px-3 py-3 text-xs font-bold"
              style={{
                color: route.textColor || mobile_menu_text_color,
                backgroundColor: active ? active_menu_color : "transparent",
              }}
            >
              {route.label}
            </Link>
          );
        }

        return (
          <div
            key={route.id}
            className="w-full border-b"
            style={{ borderColor: border_color }}
          >
            <button
              type="button"
              onClick={() =>
                setOpenMobileAccordion(
                  openMobileAccordion === route.id ? null : route.id,
                )
              }
              className="flex w-full cursor-pointer items-center justify-between rounded-sm p-3 text-[13px]"
              style={{ color: mobile_menu_text_color }}
              aria-expanded={openMobileAccordion === route.id}
            >
              {route.label}
              <ChevronRight
                open={openMobileAccordion === route.id}
                color={mobile_menu_text_color}
              />
            </button>

            {openMobileAccordion === route.id ? (
              <div className="flex flex-col">
                {route.children.map((child) => {
                  const hasGrandChildren = child.children.length > 0;

                  return (
                    <div key={child.id} className="w-full">
                      {hasGrandChildren ? (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedChild(
                              expandedChild === child.id ? null : child.id,
                            )
                          }
                          className="flex w-full cursor-pointer items-center justify-between rounded-sm pr-3 text-xs"
                          style={{ color: child.textColor || dropdown_text_color }}
                          aria-expanded={expandedChild === child.id}
                        >
                          <span className="flex-1 py-2 pl-4 pr-2 text-left">
                            {child.label}
                          </span>
                          <ChevronRight
                            open={expandedChild === child.id}
                            color={child.textColor || dropdown_text_color}
                          />
                        </button>
                      ) : (
                        <Link
                          href={child.route}
                          target={isExternal(child.route) ? "_blank" : undefined}
                          rel={
                            isExternal(child.route) ? "noopener noreferrer" : undefined
                          }
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block cursor-pointer rounded-sm px-8 py-2 text-xs"
                          style={{ color: child.textColor || dropdown_text_color }}
                        >
                          {child.label}
                        </Link>
                      )}

                      {hasGrandChildren && expandedChild === child.id ? (
                        <div
                          className="flex w-full flex-col items-start space-y-2 border-y px-0 py-1"
                          style={{ borderColor: border_color }}
                        >
                          {child.children.map((grandChild) => (
                            <Link
                              key={grandChild.id}
                              href={grandChild.route}
                              onClick={() => {
                                setExpandedChild(null);
                                setIsMobileMenuOpen(false);
                              }}
                              className="w-full cursor-pointer px-5 py-1 text-xs"
                              style={{ color: grandChild.textColor || SUBITEM_TEXT }}
                            >
                              {grandChild.label}
                            </Link>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  return (
    <header
      className={cn(
        "w-full",
        /* `relative` and `sticky` must not both apply — the last Tailwind
           utility wins and breaks sticky. z-[90] is the sticky-nav layer, the
           same one NavBar 4 / 1047 use: above Promo Pill's z-[85], below the
           z-[100] contact strip. */
        isPreview || !stickyToTop ? "relative" : "sticky z-[90]",
        showBarShadow && "shadow-md",
      )}
      style={{
        backgroundColor: background_color,
        /* Sits below a pinned promo strip, reproducing v1's `top-[42px]`.
           Header 1046 / 5 / 7 publish their height into this property. */
        ...(isPreview || !stickyToTop
          ? {}
          : { top: "var(--header4-sticky-height, 0px)" }),
      }}
    >
      {/* ---------- Desktop + tablet bar (≥ 768px) ---------- */}
      <div className="mx-auto hidden h-16 max-w-[2024px] tab:block sm:h-[65px]">
        <div className="flex h-full items-center justify-between gap-3">
          {logoGroup("shrink-0 px-5 sm:pl-6", "hidden sm:block")}

          <nav ref={navRef} className="flex h-full items-center tab:space-x-2 sm:gap-1 sm:space-x-0">
            {routes.map((route) => {
              const active = isRouteActive(route.route);

              if (route.children.length === 0) {
                return (
                  <Link
                    key={route.id}
                    href={route.route}
                    target={isExternal(route.route) ? "_blank" : undefined}
                    rel={isExternal(route.route) ? "noopener noreferrer" : undefined}
                    className="cursor-pointer rounded-sm px-3 py-1"
                    style={{
                      backgroundColor: active ? active_menu_color : "transparent",
                    }}
                  >
                    <p
                      className="text-[13px] font-semibold"
                      style={{ color: route.textColor || nav_text_color }}
                    >
                      {route.label}
                    </p>
                  </Link>
                );
              }

              const open = openDropdown === route.id;

              return (
                <div key={route.id} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdown(open ? null : route.id);
                      setExpandedChild(null);
                    }}
                    className="cursor-pointer rounded-sm px-3 py-1"
                    style={{
                      backgroundColor:
                        open || active ? active_menu_color : "transparent",
                    }}
                    aria-expanded={open}
                  >
                    <span
                      className="flex items-center justify-between text-[13px] font-semibold"
                      style={{ color: route.textColor || nav_text_color }}
                    >
                      {route.label}
                      <ChevronDown
                        open={open}
                        color={route.textColor || nav_text_color}
                      />
                    </span>
                  </button>
                  {open ? renderDropdown(route) : null}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center justify-end gap-2 px-5 sm:pr-6">
            {applyCta()}
          </div>
        </div>
      </div>

      {/* ---------- Mobile bar (< 768px) ---------- */}
      <div className="relative flex h-[50px] w-full items-center justify-between tab:hidden">
        <div className="flex items-center space-x-3 px-3">
          {routes.length > 0 ? (
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="flex shrink-0 items-center justify-center"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                mobile_cancel_icon ? (
                  <Image
                    src={mobile_cancel_icon}
                    alt={mobile_cancel_icon_alt}
                    width={28}
                    height={28}
                    className="object-contain"
                    unoptimized={shouldUnoptimizeImageSrc(mobile_cancel_icon)}
                  />
                ) : (
                  <CloseGlyph color={nav_text_color} />
                )
              ) : hamburger_icon ? (
                <Image
                  src={hamburger_icon}
                  alt={hamburger_icon_alt}
                  width={28}
                  height={28}
                  className="object-contain"
                  unoptimized={shouldUnoptimizeImageSrc(hamburger_icon)}
                />
              ) : (
                <HamburgerGlyph color={nav_text_color} />
              )}
            </button>
          ) : null}
          {logoGroup("", "")}
        </div>

        <div className="flex items-center justify-end space-x-3 px-3">
          {applyCta()}
        </div>

        {isMobileMenuOpen ? renderMobileMenu() : null}
      </div>
    </header>
  );
};

export default NavBar1060;
